/**
 * FeaturedProjectsSection — landing-page strip that surfaces the latest public
 * Pro Projects with their hero image. Designed to act as the "social proof" /
 * "live work" section investors and acquirers see right on the homepage.
 *
 * Behaviour:
 *   • Fetches /api/projects/public-gallery?limit=6
 *   • Hides itself if there are no public projects yet (no empty state on home)
 *   • Each card → /projects/:id (public detail page with all 4-6 views)
 *   • "View all" CTA → /gallery
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, BadgeCheck, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { API_URL } from '@/utils/apiUrl';


const FidelityPill = ({ score }) => {
  if (typeof score !== 'number') return null;
  const tone = score >= 80 ? 'emerald' : score >= 60 ? 'cyan' : 'amber';
  const p = {
    emerald: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10',
    cyan:    'border-cyan-500/40    text-cyan-300    bg-cyan-500/10',
    amber:   'border-amber-500/40   text-amber-300   bg-amber-500/10',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${p}`}>
      <BadgeCheck className="h-3 w-3" />
      {score}%
    </span>
  );
};

export default function FeaturedProjectsSection() {
  const { language } = useLanguage();
  const isTr = language === 'tr';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/projects/public-gallery?limit=6`);
        const d = await r.json();
        if (!alive) return;
        setProjects((d.projects || []).filter(p => !!p.concept_image));
      } catch {
        if (alive) setProjects([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Hide section entirely if there is nothing meaningful to show
  if (!loading && projects.length === 0) return null;

  return (
    <section
      data-testid="featured-projects-section"
      className="relative w-full px-6 py-24 lg:px-12 lg:py-32 bg-[#060B1A] overflow-hidden"
    >
      {/* ambient gradient */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute right-0 bottom-1/4 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-cyan-300">
              <Sparkles className="h-3 w-3" />
              {isTr ? 'Açıklamalı Proje Galerisi' : 'Labeled Project Gallery'}
            </div>
            <h2 className="text-4xl font-bold leading-tight text-white sm:text-5xl">
              {isTr ? (
                <>Platformda <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-emerald-300">üretilenler</span></>
              ) : (
                <>Built on the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-emerald-300">platform</span></>
              )}
            </h2>
            <p className="mt-3 text-base text-white/60 max-w-xl">
              {isTr
                ? 'Herkese açık projeler ve açıkça etiketlenmiş demo örnekleri. Çıktı kullanılabilirliği projeye göre değişir; tüm mühendislik çıktıları insan incelemesi gerektirir.'
                : 'Public projects and clearly labeled demo examples. Artifact availability varies by project; all engineering outputs require human review.'}
            </p>
          </div>
          <Link
            to="/projects"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-white/90 hover:border-cyan-400/40 hover:text-cyan-200 transition-all"
          >
            {isTr ? 'Tüm projeler' : 'View all projects'}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex h-64 items-center justify-center text-white/40" data-testid="featured-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => {
              const title = (p.project_data?.project_summary?.title) || p.prompt?.slice(0, 60) || 'Untitled project';
              const subtitle = p.project_data?.project_summary?.subtitle || '';
              const fidScore = p.fidelity?.score;
              const viewCount = Object.keys(p.concept_views || {}).length;
              return (
                <motion.div
                  key={p.project_id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  data-testid={`featured-card-${i}`}
                >
                  <Link
                    to={`/projects/${p.project_id}`}
                    className="group block overflow-hidden rounded-2xl border border-white/10 bg-[#0A0F1F] transition-all hover:border-cyan-400/40 hover:shadow-[0_0_40px_-15px_rgba(34,211,238,0.45)]"
                  >
                    {/* Hero image */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-cyan-500/5 to-emerald-500/5">
                      {p.concept_image ? (
                        <img
                          src={p.concept_image}
                          alt={title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="h-full w-full grid place-items-center bg-gradient-to-br from-cyan-500/10 to-violet-500/10" aria-hidden="true">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-white/30">Nexus AI</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5">
                        {fidScore != null && <FidelityPill score={fidScore} />}
                        {viewCount > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/40 backdrop-blur px-2 py-0.5 text-[10px] font-bold text-white/80">
                            <Layers className="h-3 w-3" />
                            {viewCount} {isTr ? 'görünüm' : 'views'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="p-5">
                      <h3 className="text-base font-bold text-white line-clamp-1 group-hover:text-cyan-200 transition-colors">
                        {title}
                      </h3>
                      {subtitle && (
                        <p className="mt-1 text-xs text-white/45 line-clamp-2 leading-relaxed">{subtitle}</p>
                      )}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-white/30">
                          {p.project_type || 'product'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isTr ? 'Aç' : 'Open'}
                          <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
